<template>
    <div v-view="trackHeatMap" class="relative h-full w-full">

        <img :src="imgSrc" class="object-contain w-full h-full" alt="Trucking Heatmap">

        <div v-for="(dot, index) in dots" :key="getUniqueKey(index)" :style="toStyle(dot)" class="dot" />

    </div>
</template>

<script>
export default {
    name: 'TruckingHeatMap',

    props: {
        imgSrc: {
            type: String,
            required: true
        },
        startNumber: {
            type: Number,
            default: 0
        },
        targetNumber: {
            type: Number,
            default: 917210
        }
    },

    data() {
        return {
            validCoords: [
                [0.5, 0.1], // middle of nowhere
                [0.7, 0.25], // detroit
                [0.87, 0.25], // east coast
                [0.85, 0.35],
                [0.75, 0.35], // Ohio
                [0.3, 0.38],
                [0.2, 0.28], // Somewhere in the desert
                [0.21, 0.63], // new mexico?
                [0.7, 0.53], // LA
                [0.5, 0.37], // norcal
                [0.5, 0.3],
                [0.6, 0.2],
                [0.45, 0.77], // texas
                [0.65, 0.57], // tennessee?
                [0.82, 0.87], // florida
                [0.8, 0.83], // florida
                [0.8, 0.6], // virginia
                [0.7, 0.5],
                [0.07, 0.09], // Seattle area
            ],
            maxIncrement: 1,
            dots: [],
            mapInViewport: false,
            currentNumber: this.startNumber,
        }
    },

    watch: {
        mapInViewport(newVal, oldVal) {
            if(newVal) {
                // Starts animating numbers
                this.animateDots()
            }
        }
    },

    methods: {
        getUniqueKey(index) {
            return index + '_' + Math.random();
        },
        animateDots() {
            this.tween(
                this.startNumber, 
                this.targetNumber, 
                1000, 
                this.quadLerp, 
                number => this.currentNumber = number, 
                finalValue => {
                    // The callback is called when the initial tween is done,
                    // from here we increase by one or two, and show dots on the map.

                    const showDots = () => {
                        // we add "maxIncrement" to the count and the same amount of dots are displayed below
                        const increment = Math.round(Math.random() * this.maxIncrement);
                        finalValue += increment;
                        // We refresh with no dots, so animations are reset and new elements are created
                        this.dots = [ ...this.getCoords(increment) ];
                        // here we update the counter
                        // We use the callback parameter "finalValue" because the scope captured the initial value of "currentNumber"
                        this.currentNumber = finalValue;
                    };
                    // Interval won't trigger for 2 seconds, but we want to add a dot to the map right away
                    // before users scroll and miss it...
                    showDots();
                    window.setInterval(showDots, 2000);
                }
            );
        },
        quadLerp(from, to, weight) {
            this.lerp(from, to, Math.pow(weight, 4))
        },
        lerp(from, to, weight) {
            from + (to - from) * weight
        },
        tween( from, to, duration, lerpFunc, setter, callback){
            if (this.mapInViewport) {
                const now = Date.now();
                
                const interval = window.setInterval(() => {
                    // Compute tween weight by comparing initial timestamp to now
                    const then = Date.now() - now;
                    const weight = Math.min(1.0, (then / duration));
                    // interpolates weight between selected values
                    const interpolated = lerpFunc(from, to, weight);
                    // This is the setter from useState in MapWithCounter
                    setter(Math.round(interpolated));
                }, 30);

                // kills the interval once done
                window.setTimeout(() => {
                    // we stop the tweening
                    window.clearInterval(interval);
                    // To prevent rounding, timing errors, we set the final value
                    setter(Math.round(to));
                    callback.call(undefined, to);
                }, duration);
            }
        },
        getCoords(count = 0) {
            // 0 - validCoords. don't use a value of count > 3-4
            const cleanCount = Math.min(
                this.validCoords.length,
                Math.max(0, Math.floor(count))
            );
            const maxIndex = this.validCoords.length - 1;
            const indexes = [];
            while (indexes.length < cleanCount) {
                const randomIndex = Math.floor(Math.random() * maxIndex);
                if (!indexes.includes(randomIndex)) {
                    indexes.push(randomIndex);
                }
            }
            return indexes.map(i => {
                const [x, y] = this.validCoords[i];
                return {x , y}
            });
        },

        trackHeatMap(el) {
            if (el.type === 'enter') {
                this.mapInViewport = true;
            } else if (el.type === 'exit') {
                this.mapInViewport = false;
            }
        },

        toStyle(dot) {
            return {
                borderRadius: '50%',
                height: '20px',
                left: `${dot.x * 100}%`,
                position: 'absolute',
                top: `${dot.y * 100}%`,
                width: '20px',
                opacity: '1'
            };
        }
    },
}

</script>

<style scoped>
@keyframes dotFadeOut {
  0% {
    opacity: 0.3;
    transform: scale(10);
  }

  100% {
    opacity: 1;
    transform: scale(0.2);
  }
}

.dot {
  background: var(--primary-darker);
  opacity: 0;
  animation-name: dotFadeOut;
  animation-duration: 2s;
  animation-timing-function: ease-in-out;
  animation-direction: normal;
}
</style>